import React, { useRef } from "react";
import { FieldOptionsType, MappedValueType } from "../types";
import { Box, IconButton } from "@mui/material";
import AcxSelect from "components/UI/Select/BaseSelectComponents/AcxSelect";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import SubdirectoryArrowRightOutlinedIcon from "@mui/icons-material/SubdirectoryArrowRightOutlined";
import { arrowStyles, selectCustomStyle } from "../helpers/componentUtils";
import AcxMainTextField from "components/UI/AcxMainTextField";
import MinusIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import theme from "Theme/AppTheme";
import useHoverAction from "../hooks/useHoverAction";
import DeleteButton from "../components/DeleteButton";

type MappedValueProps = {
    mappedValue: MappedValueType;
    targetOptions: FieldOptionsType[];
    updatedTargetField: (value: string) => void;
    updatedMappedPair: (key: string, value: string, id: string) => void;
    addMappedPair: () => void;
    removeMappedPair: (id: string) => void;
    removeMappedValue: () => void;
};

const MappedValue: React.FC<MappedValueProps> = ({
    mappedValue,
    targetOptions,
    updatedTargetField,
    updatedMappedPair,
    addMappedPair,
    removeMappedPair,
    removeMappedValue,
}) => {
    const { targetField, pairs, id } = mappedValue;

    const containerRef = useRef<HTMLDivElement>(null);
    const { showAction, handlers } = useHoverAction(containerRef);

    return (
        <Box
            ref={containerRef}
            {...handlers}
            sx={{
                display: "flex",
                alignItems: "flex-start",
                background: theme.palette.white.main,
                borderRadius: 2,
                padding: 1,
                gap: 1,
            }}
        >
            <AcxSelect
                customStyle={selectCustomStyle({ flex: 1 })}
                fullWidth
                id={`mapped-value-field-${id}`}
                options={targetOptions}
                placeholder="Select"
                defaultValue={targetOptions.find(
                    (e) => e.label === targetField,
                )}
                onChange={(e) => updatedTargetField(e.value)}
            />
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 3,
                    gap: 1,
                }}
            >
                {pairs.map(({ sourceValue, targetValue, id }, index) => (
                    <Box
                        key={id}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            flex: 2,
                            gap: 1,
                        }}
                    >
                        <SubdirectoryArrowRightOutlinedIcon sx={arrowStyles} />
                        <AcxMainTextField
                            fullWidth
                            id={`mapped-value-source-${id}`}
                            value={sourceValue}
                            placeholderText="Enter Source Value..."
                            onChange={(e) =>
                                updatedMappedPair(
                                    "sourceValue",
                                    e.target.value,
                                    id,
                                )
                            }
                            containerStyle={{ flex: 9 }}
                        />
                        <ArrowForwardOutlinedIcon sx={arrowStyles} />
                        <AcxMainTextField
                            fullWidth
                            id={`mapped-value-target-${id}`}
                            value={targetValue}
                            placeholderText="Enter Target Value..."
                            onChange={(e) =>
                                updatedMappedPair(
                                    "targetValue",
                                    e.target.value,
                                    id,
                                )
                            }
                            containerStyle={{ flex: 9 }}
                        />
                        {index === 0 && (
                            <>
                                <IconButton
                                    size="small"
                                    onClick={addMappedPair}
                                    sx={{
                                        padding: theme.spacing(0.5),
                                        height: "fit-content",
                                    }}
                                >
                                    <AddIcon fontSize="inherit" />
                                </IconButton>
                                <DeleteButton
                                    onDelete={removeMappedValue}
                                    showDelete={showAction}
                                />
                            </>
                        )}

                        {index !== 0 && (
                            <IconButton
                                size="small"
                                onClick={() => removeMappedPair(id)}
                                sx={{
                                    padding: theme.spacing(0.5),
                                    height: "fit-content",
                                    marginRight: 3.75,
                                }}
                            >
                                <MinusIcon fontSize="inherit" />
                            </IconButton>
                        )}
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export default MappedValue;
